// @flow

import { type Descriptor, createDescriptor } from '../../../controls/router';
import { type Language } from '../../../language';
import { type CompositePropertyType } from './propertyTypeFilterChoices';

export type SortParams = {|
  sortBy: null | string,
  sortDirection: null | string,
|};

export const sortDescriptor: Descriptor<SortParams> = createDescriptor({
  sortBy: null,
  sortDirection: null,
});

export type GrossRentParams = {|
  grossRentMonthly_lte: null | number,
  grossRentMonthly_gte: null | number,
|};

export const grossRentDescriptor: Descriptor<GrossRentParams> =
  createDescriptor({
    grossRentMonthly_lte: null,
    grossRentMonthly_gte: null,
  });

export type SalePriceParams = {|
  salePrice_lte: null | number,
  salePrice_gte: null | number,
|};

export const salePriceDescriptor: Descriptor<SalePriceParams> =
  createDescriptor({ salePrice_lte: null, salePrice_gte: null });

export type SalePricePerSurfaceParams = {|
  salePricePerLivingSurface_lte: null | number,
  salePricePerLivingSurface_gte: null | number,

  salePricePerLandSurface_lte: null | number,
  salePricePerLandSurface_gte: null | number,
|};

export const salePricePerSurfaceDescriptor: Descriptor<SalePricePerSurfaceParams> =
  createDescriptor({
    salePricePerLivingSurface_lte: null,
    salePricePerLivingSurface_gte: null,

    salePricePerLandSurface_lte: null,
    salePricePerLandSurface_gte: null,
  });

export const salePricePerLivingSurfaceDescriptor: Descriptor<{|
  salePricePerLivingSurface_lte: null | number,
  salePricePerLivingSurface_gte: null | number,
|}> = createDescriptor({
  salePricePerLivingSurface_lte: null,
  salePricePerLivingSurface_gte: null,
});

export const salePricePerLandSurfaceDescriptor: Descriptor<{|
  salePricePerLandSurface_lte: null | number,
  salePricePerLandSurface_gte: null | number,
|}> = createDescriptor({
  salePricePerLandSurface_lte: null,
  salePricePerLandSurface_gte: null,
});

export type RentPricePerAdditionalParams = {|
  grossRentM2yearly_lte: null | number,
  grossRentM2yearly_gte: null | number,
  grossRentRoomMonthly_lte: null | number,
  grossRentRoomMonthly_gte: null | number,
|};

export const rentPricePerAdditionalDescriptor: Descriptor<RentPricePerAdditionalParams> =
  createDescriptor({
    grossRentM2yearly_lte: null,
    grossRentM2yearly_gte: null,
    grossRentRoomMonthly_lte: null,
    grossRentRoomMonthly_gte: null,
  });
export type OfferTypeParams = {|
  offerType: 'buy' | 'rent',
|};

export const offerTypeDescriptor: Descriptor<OfferTypeParams> =
  createDescriptor({ offerType: 'buy' });

export type PlaceSlug = {|
  +slug: string,
  +lang: Language,
|};

export type PlacesSlugs = $ReadOnlyArray<PlaceSlug>;

export type PlacesParams = {|
  placeSlugs: PlacesSlugs,
|};

export const placesDescriptor: Descriptor<PlacesParams> = createDescriptor({
  placeSlugs: [],
});

export type PageParams = {|
  page: number,
|};

export const pageDescriptor: Descriptor<PageParams> = createDescriptor({
  page: 0,
});

export type PropertyTypeFilterParams = {|
  compositePropertyType_eq: null | CompositePropertyType,
|};

export const propertyTypeFilterDescriptor: Descriptor<PropertyTypeFilterParams> =
  createDescriptor({ compositePropertyType_eq: null });

export type MapBoundsParams = {|
  north: number | null,
  south: number | null,
  east: number | null,
  west: number | null,
|};

export const mapBoundsDescriptor: Descriptor<MapBoundsParams> =
  createDescriptor({
    north: null,
    south: null,

    east: null,
    west: null,
  });

export type ShowMapParams = {|
  showMap: boolean | null,
|};

export const showMapDescriptor: Descriptor<ShowMapParams> = createDescriptor({
  showMap: null,
});

export type ListingSlugParams = {|
  listingSlug: string,
|};

export const listingSlugDescriptor: Descriptor<ListingSlugParams> =
  createDescriptor({
    listingSlug: '',
  });

export type OfferTypeFilterParams = {|
  ...GrossRentParams,
  ...SalePriceParams,
  ...OfferTypeParams,
  ...SalePricePerSurfaceParams,
  ...RentPricePerAdditionalParams,
|};

export const offerTypeFilterDescriptor: Descriptor<OfferTypeFilterParams> =
  createDescriptor(
    grossRentDescriptor,
    salePriceDescriptor,
    offerTypeDescriptor,
    salePricePerSurfaceDescriptor,
    rentPricePerAdditionalDescriptor,
  );

export type RoomsFilterParams = {|
  numberOfRooms_gte: null | number,
  numberOfRooms_lte: null | number,
|};

export const roomsFilterDescriptor: Descriptor<RoomsFilterParams> =
  createDescriptor({
    numberOfRooms_gte: null,
    numberOfRooms_lte: null,
  });

export type PortalFilterParams = {|
  portal_in: $ReadOnlyArray<string>,
|};

export const portalFilterDescriptor: Descriptor<PortalFilterParams> =
  createDescriptor({
    portal_in: [],
  });

export type AgencyFilterParams = {|
  agencyReference_contains: null | string,
|};

export const agencyFilterDescriptor: Descriptor<AgencyFilterParams> =
  createDescriptor({
    agencyReference_contains: null,
  });

export type AgencyNameFilterParams = {|
  agencyNames: null | string,
|};

export const agencyNameFilterDescriptor: Descriptor<AgencyNameFilterParams> =
  createDescriptor({
    agencyNames: null,
  });

export type OnlineSinceFilterParams = {|
  createdAt_gte: ?string,
|};

export const onlineSinceFilterDescriptor: Descriptor<OnlineSinceFilterParams> =
  createDescriptor({
    createdAt_gte: null,
  });

export type LivingSurfaceFilterParams = {|
  livingSurface_lte: null | number,
  livingSurface_gte: null | number,
|};

export const livingSurfaceFilterDescriptor: Descriptor<LivingSurfaceFilterParams> =
  createDescriptor({
    livingSurface_lte: null,
    livingSurface_gte: null,
  });

export type LandSurfaceFilterParams = {|
  landSurface_lte: null | number,
  landSurface_gte: null | number,
|};

export const landSurfaceFilterDescriptor: Descriptor<LandSurfaceFilterParams> =
  createDescriptor({
    landSurface_lte: null,
    landSurface_gte: null,
  });

export type SurfaceFilterParams = {|
  ...LivingSurfaceFilterParams,
  ...LandSurfaceFilterParams,
|};

export const surfaceFilterDescriptor: Descriptor<SurfaceFilterParams> =
  createDescriptor(livingSurfaceFilterDescriptor, landSurfaceFilterDescriptor);

export type TopFilterParams = PlacesParams;

export const topFilterDescriptor = placesDescriptor;

type EquipmentFilterParams = {|
  isNewDevelopment_eq: null | boolean,
  hasBalcony_eq: null | boolean,
  hasGarden_eq: null | boolean,
  hasParking_eq: null | boolean,
  hasElevator_eq: null | boolean,
  hasView_eq: null | boolean,
  hasFireplace_eq: null | boolean,
  hasGarage_eq: null | boolean,
  isChildFriendly_eq: null | boolean,
  isWheelchairAccessible_eq: null | boolean,
  isMinergieCertified_eq: null | boolean,
  isFurnished_eq: null | boolean,
  bookOnline_eq: null | boolean,
  isFsbo_eq: null | boolean,
|};

export const equipmentFilterDescriptor: Descriptor<EquipmentFilterParams> =
  createDescriptor({
    isFurnished_eq: null,
    bookOnline_eq: null,
    isNewDevelopment_eq: null,
    hasBalcony_eq: null,
    hasGarden_eq: null,
    hasParking_eq: null,
    hasElevator_eq: null,
    hasView_eq: null,
    hasFireplace_eq: null,
    hasGarage_eq: null,
    isChildFriendly_eq: null,
    isWheelchairAccessible_eq: null,
    isMinergieCertified_eq: null,
    isFsbo_eq: null,
  });

export type OtherFilterParams = {|
  ...EquipmentFilterParams,
  ...PortalFilterParams,
  ...AgencyFilterParams,
  ...AgencyNameFilterParams,
  ...OnlineSinceFilterParams,
|};

export const otherFilterDescriptor: Descriptor<OtherFilterParams> =
  createDescriptor(
    equipmentFilterDescriptor,
    portalFilterDescriptor,
    agencyFilterDescriptor,
    agencyNameFilterDescriptor,
    onlineSinceFilterDescriptor,
  );

export type PriceFilterParams = {|
  ...GrossRentParams,
  ...SalePriceParams,
|};

export const priceFilterDescriptor: Descriptor<PriceFilterParams> =
  createDescriptor(grossRentDescriptor, salePriceDescriptor);
